// ANGEL FLIGHT — GameView3D: the concrete Three.js renderer seam.
//
// Owns the WebGLRenderer + Scene + the gunship sensor camera, and implements the
// GameView contract the simulation systems talk to (worldToScreen / screenToNdc).
// The thermal post-processing composer is layered on in Stage 3; here render()
// draws the raw scene so the canvas is alive.

import * as THREE from 'three';
import { BALANCE } from '../config/balance';
import { RENDER } from '../config/render';
import type { Vec2, WorldPos } from '../core/types';
import { worldToThree } from './coords';
import type { EnemyHit, GameView, ScreenProjection } from './GameView';
import type { GunView3D } from './GunView3D';
import { RenderPipeline, type RenderMode } from './RenderPipeline';

/** Anything that can answer an enemy raycast (ColorWorld in-game). */
interface HitProvider {
  raycast(ndcX: number, ndcY: number, camera: THREE.Camera): EnemyHit | null;
}

export class GameView3D implements GameView {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly domElement: HTMLCanvasElement;
  readonly pipeline: RenderPipeline;

  // Logical view size we project into (matches the 1280×720 sim/aim space so the
  // reused screen-space clamps + FiringSystem rects stay valid).
  width = BALANCE.VIEW.width;
  height = BALANCE.VIEW.height;

  private readonly tmp = new THREE.Vector3();
  private readonly tmpAbove = new THREE.Vector3();
  private hitProvider: HitProvider | null = null;
  /** The foreground gun viewmodel, drawn as an overlay after the world. */
  private gun: GunView3D | null = null;
  // Flight motion lives on the CAMERA (roll its up-vector for bank, wander the
  // aim for sway) so the world content needs no reparenting — the eye is the
  // aircraft. Banks/sways read as "circling gunship".
  private readonly baseAim = new THREE.Vector3();
  private flightT = 0;

  constructor(parent: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true, // color mode benefits from MSAA on the world edges
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = RENDER.grade.toneMappingExposure;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.autoClear = false; // we draw world (composer) then gun overlay
    this.domElement = this.renderer.domElement;
    this.domElement.style.width = '100%';
    this.domElement.style.height = '100%';
    this.domElement.style.display = 'block';
    parent.appendChild(this.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(RENDER.fog.color);
    this.scene.fog = new THREE.Fog(RENDER.fog.color, RENDER.fog.near, RENDER.fog.far);

    const c = RENDER.camera;
    this.camera = new THREE.PerspectiveCamera(c.fovDeg, 1, c.nearM, c.farM);
    this.camera.position.set(0, c.altitudeM, c.setbackM);
    // Raised aim point (lookAtYM) tips the pitch up so the horizon + skyline +
    // a sliver of sky sit in the top of the frame (G2 reframe).
    const aim = worldToThree({ x: 0, y: c.lookAtYM, z: c.lookAtZ });
    this.baseAim.set(aim.x, aim.y, aim.z);
    this.camera.lookAt(this.baseAim);

    // The post chain — color grade by default, thermal as a runtime toggle.
    this.pipeline = new RenderPipeline(this.renderer, this.scene, this.camera, RENDER.defaultMode);

    this.resize(parent.clientWidth, parent.clientHeight);
  }

  /** Attach the foreground gun overlay (built by App). The pipeline also gets
   *  it so THERMAL mode can pull the gun into the sensor feed (graded). */
  setGun(gun: GunView3D): void {
    this.gun = gun;
    this.pipeline.setOverlay(gun.scene, gun.camera);
  }

  /** The LIVE barrel-tip screen anchor (NDC) this frame — the gun's
   *  muzzleScreenAnchor projected through the overlay camera, tracking swing +
   *  recoil. Null when no gun is attached (headless tests). Juice3D spawns
   *  tracers from here instead of a fixed bottom-centre point (G2). */
  muzzleNdc(): { nx: number; ny: number } | null {
    return this.gun ? this.gun.muzzleScreenAnchor() : null;
  }

  /** Toggle the visual mode (color ↔ thermal). */
  setMode(mode: RenderMode): void {
    this.pipeline.setMode(mode);
  }
  getMode(): RenderMode {
    return this.pipeline.getMode();
  }

  /** Pre-compile every shader program for the world scene AND the gun overlay
   *  scene (synchronous, once, at mission start — behind the DEPLOY tap) so the
   *  FIRST trigger pull never hitches on a GPU program build. Pair with the
   *  Juice3D prewarm transients so the VFX programs are in the scene when this
   *  runs. */
  warmup(): void {
    this.renderer.compile(this.scene, this.camera);
    if (this.gun) this.renderer.compile(this.gun.scene, this.gun.camera);
  }

  /** Resize the drawing buffer + cameras + composer to the container CSS size. */
  resize(cssWidth: number, cssHeight: number): void {
    const w = Math.max(1, cssWidth | 0);
    const h = Math.max(1, cssHeight | 0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.pipeline.setSize(w, h);
    this.gun?.resize(w, h);
  }

  /** Draw one frame: bank the flight rig, render the world through the post
   *  chain, then composite the gun overlay on top. `deltaSec` advances effects. */
  render(deltaSec: number): void {
    this.flightT += deltaSec;
    const f = RENDER.flight;
    // Bank: roll the camera up-vector. Sway: wander the aim point laterally so
    // you drift past targets and must re-acquire — the core "I'm flying" cue.
    const roll = Math.sin((this.flightT / f.bankPeriodS) * Math.PI * 2) * f.bankAmpRad;
    const sway = Math.cos((this.flightT / f.swayPeriodS) * Math.PI * 2) * f.swayAmpM;
    this.camera.up.set(Math.sin(roll), Math.cos(roll), 0);
    this.tmp.copy(this.baseAim);
    this.tmp.x += sway;
    this.camera.lookAt(this.tmp);

    this.renderer.clear(); // color + depth (autoClear is off)
    this.pipeline.render(deltaSec); // world → post → screen
    // COLOR mode: the gun composites on top, ungraded (the shipped look).
    // THERMAL mode: the pipeline already drew it INSIDE the FLIR feed.
    if (!this.pipeline.ownsOverlay) this.gun?.renderOverlay(this.renderer);
  }

  /**
   * Project a world point to logical view-space pixels. `scale` is
   * screen-px-per-metre at that depth (FiringSystem rect contract). Computed by
   * projecting a point 1m above and taking the vertical pixel delta.
   */
  worldToScreen(p: WorldPos): ScreenProjection {
    const t = worldToThree(p);
    this.tmp.set(t.x, t.y, t.z).project(this.camera);
    const above = worldToThree({ x: p.x, y: p.y + 1, z: p.z });
    this.tmpAbove.set(above.x, above.y, above.z).project(this.camera);

    const visible = this.tmp.z > -1 && this.tmp.z < 1;
    const sx = (this.tmp.x * 0.5 + 0.5) * this.width;
    const sy = (1 - (this.tmp.y * 0.5 + 0.5)) * this.height;
    const syAbove = (1 - (this.tmpAbove.y * 0.5 + 0.5)) * this.height;
    const scale = Math.abs(sy - syAbove); // px per metre at this depth
    return { sx, sy, scale, visible };
  }

  /** Logical view-space pixel → NDC for a raycaster (Stage 4). */
  screenToNdc(p: Vec2): { x: number; y: number } {
    return {
      x: (p.x / this.width) * 2 - 1,
      y: -((p.y / this.height) * 2 - 1),
    };
  }

  /** The world renderer registers itself here so firing can raycast the rigs. */
  setHitProvider(w: HitProvider): void {
    this.hitProvider = w;
  }

  /** Raycast enemies through a logical view-space pixel point. */
  raycastEnemy(p: Vec2): EnemyHit | null {
    if (!this.hitProvider) return null;
    const n = this.screenToNdc(p);
    return this.hitProvider.raycast(n.x, n.y, this.camera);
  }

  dispose(): void {
    this.pipeline.dispose();
    this.renderer.dispose();
    this.domElement.remove();
  }
}
